// Source: D:/AI/site-queensbutler/public/design-patterns.html — B-1a / B-1b / B-3a
import { useState as useSubState } from 'react'
import { useEffect, useRef, useState } from 'react'
import { useCompactHeader } from './useCompactHeader'
import { Link, useLocation } from 'react-router-dom'
import { navFor } from '@/data/nav.en'
import { getLang, stripEn, withLang } from '@/lib/lang'

const crest = '/img/catalog/mark_g04_gold_512.png' // Yuichi 2026-09-17「G04筆の環 … いったん、全ページのロゴをこれに差し替えろ」
const globalEnglish: Record<string, string> = {
  '/first-time/': 'First Visit', '/salon/': 'The Salon', '/studio/': 'The Studio',
  '/apprentice/': 'The Footman', '/shop/': 'The Shop', '/recruit/': 'Recruit',
}
const childEnglish: Record<string, string[]> = {
  '/first-time/': ['About', 'Rooms', 'Flow', 'Access'],
  '/salon/': ['About', 'Experience', 'Buddy', 'Price'],
  '/studio/': ['About', 'Uses', 'Space', 'Price'],
  '/apprentice/': ['About', 'Program', 'Wardrobe', 'Price'],
  '/shop/': ['New', 'Category', 'Collection', 'Guide'],
}


/** 言語スイッチ(JP | EN)。JA ページでは EN 側が /en+現在 url、EN ページでは JP 側が stripEn(url)(仕様 §2-5)。SP メニューでも共用 */
export function LangSwitch({ lang, pathname, label }: { lang: 'ja' | 'en'; pathname: string; label: string }) {
  const jaHref = stripEn(pathname)
  const enHref = withLang(pathname, 'en')
  return <span className='lang-switch' aria-label={label}>
    {lang === 'ja' ? <span className='on' aria-current='true' lang='ja'>JP</span> : <Link className='off' to={jaHref} lang='ja' hrefLang='ja' title='日本語'>JP</Link>}
    <span className='sep' aria-hidden='true'>|</span>
    {lang === 'en' ? <span className='on' aria-current='true' lang='en'>EN</span> : <Link className='off' to={enHref} lang='en' hrefLang='en' title='English'>EN</Link>}
  </span>
}

export function GlobalHeader() {
  const compact = useCompactHeader()
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [closedSub, setClosedSub] = useSubState<string | null>(null) // Yuichi 2026-09-16「サブメニューを選択した際に、サブメニューを一度閉じて画面を切り替わった感覚を」
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLElement>(null)
  const { pathname } = useLocation()
  // 言語は pathname から判定(SSG のため window 参照禁止)。ナビ・文言は nav.en.ts の navFor で切り替え、構造・CSS は共通
  const lang = getLang(pathname)
  const { globalNav, globalContact, ui: t } = navFor(lang)
  const jaPath = stripEn(pathname)
  useEffect(() => { try { const v = sessionStorage.getItem('qb_sub_closed'); if (v) { setClosedSub(v); sessionStorage.removeItem('qb_sub_closed') } } catch {} }, [pathname]) // Yuichi 2026-09-17「サブメニューのメニューを選択したらサブメニューエリアのウインドウを閉じろ…まだ治っていない」: ページ遷移後もマウスが離れるまで閉じたままにする
  useEffect(() => setOpen(false), [pathname])
  useEffect(() => {
    if (!open) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    menuRef.current?.querySelector<HTMLAnchorElement>('a')?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        menuButtonRef.current?.focus()
        return
      }
      if (event.key !== 'Tab' || !menuRef.current) return
      const focusable = [...menuRef.current.querySelectorAll<HTMLElement>('a,button')]
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable.at(-1)
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => { document.removeEventListener('keydown', onKeyDown); document.body.style.overflow = previousOverflow }
  }, [open])

  return (
    <header className={`site-header${compact ? ' is-compact' : ''}`}>
      <div className="desktop-header">
        {!compact && (
          <div className="gh-top"><div className="shell">
            {jaPath !== '/' && <Link className='gh-home' to={t.home}>{t.backToTop}</Link>}<LangSwitch lang={lang} pathname={pathname} label={t.langLabel} />
            {/* Yuichi 2026-09-17「一番上のエリアのテキストの左側に会社概要」「会社概要じゃなく、運営会社っていう表記」「会社概要にリンク」 */}
            <Link className="top-company" to={withLang('/company/', lang)}>{t.company}</Link>
            <Link className="top-cta" to={globalContact.href}>{globalContact.label}</Link>
          </div></div>
        )}
        <div className={`gh${compact ? ' compact' : ''}`}><div className="shell">
          {/* Yuichi 2026-09-17「ロゴはこれ(G04)にしてみる」「共通トップのロゴをメニュー1にいれて」「メニュー2にはフォントの左側に入れて」 */}
          <Link className="brand" to={t.home} aria-label={t.brandTop}>
            <img className="mark" src="/img/catalog/mark_g04_gold_512.png" alt="" width="512" height="476" />
            <span className="en">Queen's Butler</span>
          </Link>
          <nav aria-label={t.navGlobal}>
            {globalNav.map((item) => (
              <div className={`nav-group${closedSub === item.href ? ' closed' : ''}`} key={item.href} onMouseLeave={() => setClosedSub(null)}>
                <Link className="has" to={item.href}>{item.label}</Link>
                <div className="gh-sub nav-dropdown"><div className="shell">
                  <Link className="sub-parent" to={item.href} onClick={() => { setClosedSub(item.href); try { sessionStorage.setItem('qb_sub_closed', item.href) } catch {} }}><small>{globalEnglish[stripEn(item.href)]}</small>{item.label}{t.topSuffix}</Link>
                  {item.children.map((child, index) => <Link key={child.href} to={child.href} onClick={() => { setClosedSub(item.href); try { sessionStorage.setItem('qb_sub_closed', item.href) } catch {} }}><small>{childEnglish[stripEn(item.href)][index]}</small>{child.label}</Link>)}
                </div></div>
              </div>
            ))}
            <Link to={withLang('/recruit/', lang)}>{t.recruit}</Link>
            <Link className="btn sm" to={globalContact.href}>{globalContact.label}</Link>
          </nav>
        </div></div>
      </div>

      <div className="mobile-header">
        <div className="sph">
          <Link className="lk lk-logo" to={t.home} aria-label={t.brandTop}><img className="coronet" src="/img/catalog/mark_g04_gold_512.png" alt="" width="512" height="476" /><img className="wordmark" src="/img/catalog/wordmark_script_gold.png" alt="Queen's Butler" width="168" height="45" /></Link>
          <button ref={menuButtonRef} className="ham" type="button" aria-expanded={open} aria-controls="global-mobile-menu" aria-label={open ? t.closeNav : t.openNav} onClick={() => setOpen((value) => !value)}>
            {open ? <span aria-hidden="true">×</span> : <span aria-hidden="true"><i /><i /><i /></span>}
          </button>
        </div>
        {open && <nav ref={menuRef} className="spm" id="global-mobile-menu" aria-label={t.navMobile}>
          {globalNav.map((item, gi) => { const exp = expanded === item.href; return <div className={`spm-group${exp ? ' open' : ''}`} key={item.href}>
            <div className="spm-row">
              <Link className="spm-parent" to={item.href}>{item.label}<small>{globalEnglish[stripEn(item.href)]}</small></Link>
              <button type="button" className="spm-tg" aria-expanded={exp} aria-controls={`gm-${gi}`} aria-label={exp ? t.subClose(item.label) : t.subOpen(item.label)} onClick={() => setExpanded(exp ? null : item.href)}><i aria-hidden="true" /></button>
            </div>
            {exp && <div className="spm-children" id={`gm-${gi}`}>
              <Link className="sub-parent" to={item.href}><small>{globalEnglish[stripEn(item.href)]}</small>{item.label}{t.topSuffix}</Link>
              {item.children.map((child, index) => <Link key={child.href} to={child.href}><small>{childEnglish[stripEn(item.href)][index]}</small>{child.label}</Link>)}
            </div>}
          </div> })}
          <Link to={withLang('/recruit/', lang)}>{t.recruit}<small>{lang === 'en' ? 'Recruit' : globalEnglish['/recruit/']}</small></Link>
          <Link className="btn rose" to={globalContact.href}>{globalContact.label}</Link>
          {/* SP メニューにも言語スイッチ(仕様 §2-4/§2-5) */}
          <div className="oth spm-lang"><LangSwitch lang={lang} pathname={pathname} label={t.langLabel} /></div>
        </nav>}
      </div>
    </header>
  )
}
